import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTermsOfService,
  TermsOfService as TermsOfServiceType,
  acceptTermsOfService,
} from "../api/termsOfService";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from "../config/routes";

type ParsedTerms = TermsOfServiceType & {
  parsedContent: any;
};

export const TermsOfService = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [terms, setTerms] = useState<ParsedTerms | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const navigate = useNavigate();
  const { checkTermsAcceptance } = useAuth();

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await getTermsOfService();
      setAccepted(response.accepted);
      
      if (!response.termsOfService) {
        setTerms(null);
        return;
      }
      
      const parsedContent = typeof response.termsOfService.content === 'string' 
        ? JSON.parse(response.termsOfService.content)
        : response.termsOfService.content;
      
      setTerms({
        ...response.termsOfService,
        content: response.termsOfService.content,
        parsedContent
      });
    } catch (err) {
      setError("Failed to fetch terms of service");
      console.error("Error fetching terms of service:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = async () => {
    const retryDelays = [1000, 2000, 5000];

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const checkAcceptance = async (): Promise<boolean> => {
      const response = await getTermsOfService();
      setAccepted(response.accepted);
      return response.accepted;
    };

    try {
      setAccepting(true);
      setError(null);

      await acceptTermsOfService(terms?.version || 0);

      // Check immediately first
      let isAccepted = await checkAcceptance();

      if (!isAccepted) {
        // Retry with exponential backoff
        for (const waitTime of retryDelays) {
          await delay(waitTime);
          isAccepted = await checkAcceptance();
          if (isAccepted) break;
        }
      }

      if (isAccepted) {
        await checkTermsAcceptance();
        navigate(ROUTES.BILLING());
      } else {
        setError("We are having some problems processing your request. Please try again in a moment.");
      }
    } catch (err) {
      setError("Failed to accept terms of service");
      console.error("Error accepting terms of service:", err);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  if (error && !terms) {
    return (
      <div className="flex flex-col w-full gap-4 bg-white rounded-3xl p-4 md:max-w-[624px]">
        <div className="text-red-500 text-center">{error}</div>
      </div>
    );
  }

  if (!terms) {
    return (
      <div className="flex flex-col w-full gap-4 bg-white rounded-3xl p-4 md:max-w-[624px]">
        <div className="text-center text-gray-500">
          Terms of service not added yet
        </div>
      </div>
    );
  }

  return (
    <>
      {!accepted && (
        <div className="w-full bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Please review and accept the Terms of Service to continue.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col w-full gap-4 bg-white rounded-3xl p-4 md:max-w-[900px]">
        
        <div className="prose prose-ol:list-decimal prose-ul:list-disc max-w-none mb-6">
          {documentToReactComponents(terms.parsedContent)}
        </div>
        <div className="text-sm text-gray-500 mb-6 text-center">
          Version: {terms.version}
        </div>
        {error && (
          <div className="text-red-500 text-center mb-4">{error}</div>
        )}
        {!accepted && (
          <Button onClick={handleAcceptTerms} size="lg" disabled={accepting}>
            {accepting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 text-white" />
                Accepting...
              </span>
            ) : (
              "Accept Terms of Service"
            )}
          </Button>
        )}
      </div>
    </>
  );
};

export default TermsOfService;
